// src/components/futures/PositionCard.jsx
import React from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  Typography,
  Button,
  LinearProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import { fmt, GREEN, RED, toDateStr } from "./pnlUtils";

export default function PositionCard({ row, onClose, onCancel }) {
  const theme = useTheme();
  const borderCol = alpha(theme.palette.divider, 0.6);
  const posColor = row.position === "Long" ? GREEN : RED;

  return (
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${borderCol}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {row._symbol}
        </Typography>
        <Chip
          size="small"
          label={row.position}
          sx={{
            height: 24,
            borderRadius: 1.5,
            fontWeight: 700,
            bgcolor: alpha(posColor, 0.15),
            color: posColor,
          }}
        />
        <Chip
          size="small"
          label={row.margin_type || "—"}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={Number.isFinite(row.leverage) ? `${row.leverage}x` : "-"}
          sx={{ height: 24, borderRadius: 1.5, fontWeight: 700 }}
        />
        <Chip
          size="small"
          label={row.status}
          sx={{
            height: 24,
            borderRadius: 1.5,
            fontWeight: 700,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
          }}
        />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 900, color: (row._pnl ?? 0) >= 0 ? GREEN : RED }}
          >
            {fmt(row._pnl, 2)} USDT
          </Typography>
          <Chip
            size="small"
            label={`${fmt(row._roe, 2)}%`}
            sx={{
              height: 24,
              borderRadius: 1.5,
              fontWeight: 800,
              bgcolor: alpha((row._roe ?? 0) >= 0 ? GREEN : RED, 0.15),
              color: (row._roe ?? 0) >= 0 ? GREEN : RED,
            }}
          />
        </Box>
      </Box>

      {/* Body */}
      <Grid container spacing={1.2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
            <Row k="Qty" v={fmt(row.amount, 4)} />
            <Row k="Open" v={fmt(row.open_price, 2)} />
            <Row
              k="Current"
              v={row._current != null ? fmt(row._current, 2) : "-"}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
            <Row k="Liq. Price" v={fmt(row.liquidation_price, 2)} />
            <Row k="IM" v={`${fmt(row._im, 2)} USDT`} />
            <Row k="Type" v={row.order_type || "-"} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            variant="outlined"
            sx={{ p: 1.2, borderRadius: 2, height: "100%" }}
          >
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, fontWeight: 700, display: "block", mb: 0.5 }}
            >
              ROE
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, Math.abs(row._roe || 0)))}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: alpha(theme.palette.text.primary, 0.08),
                "& .MuiLinearProgress-bar": {
                  bgcolor: (row._roe ?? 0) >= 0 ? "#0ECB81" : "#F6465D",
                  borderRadius: 5,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, mt: 0.5, display: "block" }}
            >
              Equity Impact ≈ {(row._roe ?? 0) >= 0 ? "+" : ""}
              {fmt(row._pnl, 2)} USDT
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        {row.status === "Filled" ? (
          <Button
            startIcon={<CloseIcon />}
            variant="contained"
            size="small"
            onClick={() => onClose(row)}
            sx={{
              borderRadius: 2,
              fontWeight: 800,
              boxShadow: "none",
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              color: theme.palette.text.primary,
              "&:hover": { bgcolor: alpha("#F0B90B", 0.18) },
            }}
          >
            Close
          </Button>
        ) : (
          <Button
            startIcon={<DoNotDisturbOnIcon />}
            variant="outlined"
            size="small"
            onClick={() => onCancel(row)}
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            Cancel
          </Button>
        )}
        <Box sx={{ ml: "auto", opacity: 0.65 }}>
          <Typography variant="caption">{toDateStr(row.created_at)}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function Row({ k, v }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        fontSize: 12,
        opacity: 0.9,
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.75 }}>
        {k}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {v}
      </Typography>
    </Box>
  );
}
